import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { verifyCode } from '../services/auth';
import { useAuth } from '../services/auth/AuthContext';
import './TwoFactorAuth.css';

const TwoFactorAuth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user_id, email } = location.state || {};
  const { setUser } = useAuth();
  
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes in seconds
  const [error, setError] = useState('');
  const inputRefs = useRef([]);

  // Redirect if no user data
  useEffect(() => {
    if (!email) {
      navigate('/');
    }
  }, [email, navigate]);

  // Countdown timer
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCodeChange = (value, index) => {
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError(''); // Clear error when user types

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyPress = (e, index) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async () => {
    const verificationCode = code.join('');
    
    if (verificationCode.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setIsLoading(true);
    setError('');
    
    try {
      const data = await verifyCode(email, verificationCode);
      // Update user state in AuthContext
      setUser({ role: data.role });
      // Small delay to ensure state is updated before navigation
      setTimeout(() => {
        navigate('/dashboard');
      }, 100);
    } catch (error) {
      setError(error.message || 'Verification failed');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    // Resend endpoint not available; ask user to restart login to get a new code
    setError('Please go back and login again to receive a new code.');
  };

  const handleBackToLogin = () => {
    navigate('/');
  };

  if (!email) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="two-factor-container">
      <div className="two-factor-card">
        <div className="two-factor-header">
          <h1 className="two-factor-title">Two-Factor Authentication</h1>
          <p className="two-factor-subtitle">
            Enter the 6-digit code sent to<br />
            <span className="email-text">{email}</span>
          </p>
        </div>

        <div className="code-container">
          {code.map((digit, index) => (
            <input
              key={index}
              ref={(ref) => (inputRefs.current[index] = ref)}
              type="text"
              className={`code-input ${digit ? 'code-input-filled' : ''}`}
              value={digit}
              onChange={(e) => handleCodeChange(e.target.value, index)}
              onKeyDown={(e) => handleKeyPress(e, index)}
              maxLength={1}
              autoComplete="off"
            />
          ))}
        </div>

        <div className="timer-container">
          <p className={`timer-text ${timeLeft < 60 ? 'timer-warning' : ''}`}>
            Code expires in: {formatTime(timeLeft)}
          </p>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <button
          className={`verify-button ${isLoading || code.join('').length !== 6 ? 'verify-button-disabled' : ''}`}
          onClick={handleVerify}
          disabled={isLoading || code.join('').length !== 6}
        >
          {isLoading ? 'Verifying...' : 'Verify Code'}
        </button>

        <div className="resend-container">
          <p className="resend-text">Didn't receive the code?</p>
          <button
            className={`resend-button ${timeLeft > 0 ? 'resend-button-disabled' : ''}`}
            onClick={handleResendCode}
            disabled={isResending || timeLeft > 0}
          >
            {isResending ? 'Sending...' : 'Resend Code'}
          </button>
        </div>

        <button
          className="back-button"
          onClick={handleBackToLogin}
        >
          Back to Login
        </button>
      </div>
    </div>
  );
};

export default TwoFactorAuth;
